import React, { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";

const Dropdown = ({
  label,
  options,
  selectedOption,
  onSelect,
  className = "",
  buttonClassName = "",
  searchable = false,
  searchPlaceholder = "Search...",
  disabled = false,
  showSelectedLabel = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search when closing
  useEffect(() => {
    if (!isOpen) setSearchTerm("");
  }, [isOpen]);

  const filteredOptions =
    options?.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // Show top 5 when searchable and not searching, otherwise show all matches
  const displayOptions = filteredOptions;

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <button
        className={`px-4 py-2 bg-white border border-gray-200 rounded-lg flex items-center justify-between transition-all hover:bg-gray-50 hover:border-gray-300 text-gray-700 active:scale-[0.98] ${isOpen ? "ring-2 ring-[#881337]/5 border-[#881337] shadow-sm" : ""
          } ${disabled
            ? "opacity-50 cursor-not-allowed pointer-events-none"
            : "cursor-pointer"
          } ${buttonClassName}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        type="button"
      >
        <span className="block">
          {(showSelectedLabel && options?.find((option) => option.value === selectedOption)?.label) ||
            label}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3.5 w-3.5 ml-2 transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180 text-[#881337]" : "text-gray-400"
            }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1.5 left-0 bg-white border border-gray-100 rounded-xl shadow-[0_4px_25px_-6px_rgba(0,0,0,0.15)] overflow-hidden min-w-full w-auto max-w-[calc(100vw-2rem)]">
          {searchable && (
            <div className="p-2 border-b border-gray-50 bg-gray-50/30">
              <div className="relative">
                <input
                  type="text"
                  className="w-full h-8 pl-8 pr-3 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#881337] transition-all bg-white placeholder:text-gray-400"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                <Search
                  size={12}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>
          )}
          <div className="py-2 max-h-60 overflow-y-auto overflow-x-auto">
            {displayOptions.length > 0 ? (
              displayOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-gray-50 whitespace-normal break-words ${selectedOption === option.value
                      ? "bg-gray-50 text-[#881337] font-bold"
                      : "text-gray-600 font-medium hover:text-gray-900"
                    }`}
                  onClick={() => {
                    onSelect(option);
                    setIsOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-center">
                <p className="text-[11px] text-gray-400 font-medium">
                  No results found
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
